/**
 * The export control, shared by the crew schedule (#384) and Gap Analysis
 * (#396 slice 4).
 *
 * Both requests are the same request in two places: operations asked to
 * print or export the crew schedule, and #396 needs the gap view to leave the
 * app as a file because that is how the manpower lead's workbook has always travelled. So
 * both surfaces get the same pair of affordances in the same place, and
 * neither grows its own vocabulary for it.
 *
 * ⚠️ Print is not a lesser Excel. #384 said "printing/exporting" and the
 * printed sheet is the one a foreman can carry onto a site — it is listed
 * first for that reason.
 */

import { printFitted } from './lib/printFit';

interface Props {
  /** Builds and downloads the workbook. Synchronous — `XLSX.writeFile` is. */
  onExcel: () => void;
  /** Disabled while there is nothing loaded to export. */
  disabled?: boolean;
  /** Names the file in the button's tooltip, so it is obvious what lands. */
  what: string;
}

export default function ExportMenu({ onExcel, disabled, what }: Props) {
  return (
    // `mp-export` is the hook the print stylesheet uses to hide this control on
    // paper — a toolbar in a printout is noise.
    <div className="mp-export" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <button
        type="button"
        className="btn btn-secondary"
        style={{ fontSize: 12, padding: '5px 11px' }}
        disabled={disabled}
        title={`Print ${what}`}
        // Not window.print() directly: the crew grid is wider than a sheet at
        // some ranges, so the scale is measured first. See printFit.ts.
        onClick={() => printFitted()}
      >
        🖨 Print
      </button>
      <button
        type="button"
        className="btn btn-secondary"
        style={{ fontSize: 12, padding: '5px 11px' }}
        disabled={disabled}
        title={`Download ${what} as an Excel workbook`}
        onClick={onExcel}
      >
        ⬇ Excel
      </button>
    </div>
  );
}
